import { useMemo } from 'react'
import { useTheme } from '@mui/material'
import { useElementLabel, usePlanLabelPdfMetrics } from '@editor/hooks'
import useIsPositionPlanPreview from '@editor/pages/PlanView/hooks/useIsPositionPlanPreview'
import { useModelStore } from '@editor/stores'
import { ElementLabelDescriptor } from 'src/components/scene/ModelElementLabels/types'
import { BEAM_LABEL_BORDER_WIDTH } from './constants'
import { getPositionLabelPlacementWorld, getStoreyTopZLookup } from './labelPlacements'
import { getPdfLabelBoxDescriptorProps } from './pdfLabelBox'
import { makePlanMemberLabelLines } from './planLabelText'

/** flat text, so there is nothing to extrude */
const FLAT_TEXT_DEPTH = 0

interface Params {
  visibleModel: PlanarModel
  guidToHidden: Record<string, boolean>
  guidToCrossSection: Record<string, CrossSection>
  showRipLabels: boolean
  showLintelLabels: boolean
  /** Add the cross section as a second label line, like the plan PDF export */
  showCrossSections: boolean
}

/**
 * Rip and lintel labels for the floorplan, placed like the position-plan
 * export (`ripV2.planLabelPositionWorld` / `lintelV2.planLabelPositionWorld`):
 * offset from the element center to a fixed side. Rips sit below their rip
 * (left of it in a y-cartesian wall), lintels above (right of a y-cartesian
 * lintel), so the two label kinds never overlap. The export anchors the label
 * box's near edge at the element edge (rips) / center (lintels) plus a gap;
 * our labels are center-anchored, so half the PDF box height is added on top.
 */
const usePositionLabelDescriptors = ({
  visibleModel,
  guidToHidden,
  guidToCrossSection,
  showRipLabels,
  showLintelLabels,
  showCrossSections,
}: Params): ElementLabelDescriptor[] => {
  const { scenePalette } = useTheme()
  const getLabel = useElementLabel()
  const metrics = usePlanLabelPdfMetrics()
  // The position plan preview prints each position's lines as its PDF plan settings say.
  const isPositionPlan = useIsPositionPlanPreview()
  // All walls, not only the visible ones: a rip stays visible with its wall hidden
  // when its visibility isn't coupled to the wall's.
  const walls = useModelStore(state => state.model.walls)

  return useMemo(() => {
    if (!showRipLabels && !showLintelLabels) return []

    // Mirrors `wallIsYCartesian` on the export plan models (exact comparison,
    // diagonal walls count as x-cartesian there too).
    const wallGuidToIsYCartesian = walls.reduce((collector: Record<string, boolean>, wall) => {
      const [p0, p1] = wall.shape.points
      if (!p0 || !p1) return collector
      collector[wall.guid] = p0.x === p1.x
      return collector
    }, {})

    const storeyTopZ = getStoreyTopZLookup(visibleModel)

    return [
      ...(showRipLabels ? visibleModel.rips : []),
      ...(showLintelLabels ? visibleModel.lintels : []),
    ].reduce((collector: ElementLabelDescriptor[], position) => {
      const { position_guid: guid, wall_guid: wallGuid, is_local: isLocal, label } = position

      if (isLocal || !label || guidToHidden[guid]) return collector

      const crossSection = guidToCrossSection[guid]
      const textLines = makePlanMemberLabelLines(
        getLabel(guid),
        crossSection,
        showCrossSections,
        isPositionPlan ? position.positionPlanSettings.pdf : undefined,
      )
      if (!textLines.length) return collector

      const placement = getPositionLabelPlacementWorld({
        position,
        wallIsYCartesian: wallGuidToIsYCartesian[wallGuid],
        crossSectionHeight: crossSection?.shape?.height,
        metrics,
        numLabelLines: textLines.length,
        storeyTopZ: storeyTopZ[position.storey],
      })
      if (!placement) return collector

      const color = scenePalette.elements3d.columns as string

      collector.push({
        guid,
        textLines,
        ...placement,
        ...getPdfLabelBoxDescriptorProps(textLines, metrics),
        color,
        opacity: 1,
        textDepth: FLAT_TEXT_DEPTH,
        backgroundColor: '#FFFFFF',
        borderColor: color,
        borderWidth: BEAM_LABEL_BORDER_WIDTH,
      })

      return collector
    }, [])
  }, [
    showRipLabels,
    showLintelLabels,
    showCrossSections,
    isPositionPlan,
    visibleModel,
    walls,
    guidToHidden,
    getLabel,
    metrics,
    guidToCrossSection,
    scenePalette.elements3d.columns,
  ])
}

export default usePositionLabelDescriptors
